import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Mail, User } from "lucide-react";

export default function AccountPage() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed successfully");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Change failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-fl py-12 max-w-2xl">
      <div className="overline mb-2">Account settings</div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tighter mb-8">Your account</h1>

      <div className="card-fl p-6 mb-6" data-testid="account-info">
        <div className="flex items-center gap-2 font-bold mb-4"><User size={16}/> Profile</div>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1">Name</div>
            <div>{user?.name}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1 flex items-center gap-1"><Mail size={12}/> Email</div>
            <div>{user?.email}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1">Role</div>
            <div className="capitalize">{user?.role}</div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="card-fl p-6" data-testid="change-password-form">
        <div className="flex items-center gap-2 font-bold mb-1"><KeyRound size={16}/> Change password</div>
        <p className="text-xs text-neutral-500 mb-4">Use at least 6 characters. Include a mix of letters and numbers.</p>
        <div className="grid gap-4">
          <PwdField label="Current password" testId="current-password" value={current} onChange={setCurrent}/>
          <PwdField label="New password" testId="new-password" value={next} onChange={setNext}/>
          <PwdField label="Confirm new password" testId="confirm-password" value={confirm} onChange={setConfirm}/>
        </div>
        <button type="submit" disabled={busy} className="btn-primary mt-6" data-testid="submit-change-password">
          {busy ? "Changing…" : "Change password"}
        </button>
      </form>
    </div>
  );
}

function PwdField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">{label}</label>
      <input
        data-testid={testId}
        type="password"
        required
        minLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-11 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"
      />
    </div>
  );
}
